import { FC, useLayoutEffect, useMemo, useRef, useState } from 'react';

import pluralize from 'pluralize';

import { useAppSettingsQuery } from '@queries/AppQueries';
import {
  Entertainer,
  EntertainerFilter,
  useFilteredEntertainerIdsQuery,
  useHydratedEntertainersQuery,
} from '@queries/EntertainerQueries';

import { useRecommendEntertainerMutation } from '@mutations/CritiqueMutations';
import { useSendBulkEntertainerBadges } from '@mutations/EntertainerMutations';

import { useCurrentUserCity } from '@hooks/useCurrentUserLocation';

import { createScreenComponent } from '@utilities/createScreenComponent';

import Button from '@components/buttons/Button';
import { FontAwesomeIcon } from '@components/icons/FontAwesomeIcon';
import { AsyncImage } from '@components/layout/AsyncImg';
import Card from '@components/layout/Card/Card';
import { EmptyState } from '@components/layout/EmptyState/EmptyState';
import { LazyLoad } from '@components/layout/LazyLoad';
import { Popup } from '@components/layout/Popup/Popup';
import { Screen } from '@components/layout/Screen';
import { Skeleton } from '@components/layout/Skeleton/Skeleton';
import { TitleToolbar } from '@components/layout/TitleToolbar/TitleToolbar';

import { useEntertainerImpression } from '../EntertainerDetailsScreen/sendEntertainerDetailsPageView';

const EntertainerSupportCard: FC<{
  entertainer: Entertainer;
  onClick: (entertainer: Entertainer) => void;
}> = ({ entertainer, onClick }) => {
  const divRef = useRef(null);

  useEntertainerImpression(entertainer._id, divRef);

  const image = entertainer.avatar || entertainer.featuredMedia?.[0]?.image;
  const imageSize = 60;

  return (
    <Card style={{ borderRadius: 8 }} onClick={() => onClick(entertainer)}>
      <div ref={divRef}>
        <Card.Header
          className="p-1"
          avatar={
            <div
              style={{
                position: 'relative',
                width: imageSize,
                height: imageSize,
              }}
            >
              <div style={{ borderRadius: 8, overflow: 'hidden', width: '100%', height: '100%' }}>
                {image ? (
                  <AsyncImage
                    src={image}
                    style={{
                      width: '100%',
                      height: '100%',
                      objectFit: 'cover',
                    }}
                    placeholder={
                      <div
                        style={{
                          width: '100%',
                          height: '100%',
                          background: 'rgba(255,255,255,.2)',
                        }}
                      />
                    }
                  />
                ) : (
                  <div
                    style={{
                      width: '100%',
                      height: '100%',
                      background: 'rgba(255,255,255,.2)',
                    }}
                  />
                )}
              </div>
            </div>
          }
          tagline={entertainer.cities.map((city) => city.name).join(' • ')}
          title={entertainer.title}
          subtitle={[
            `${entertainer.badgeCount} ${pluralize('Badge', entertainer.badgeCount)}`,
            `${entertainer.featuredMedia.length} ${pluralize('Look', entertainer.featuredMedia.length)}`,
            `${entertainer.details.categories.length} ${pluralize('Category', entertainer.details.categories.length)}`,
          ].join(' • ')}
          action={
            <div className="fs-6 px-2">
              <FontAwesomeIcon name="chevron-right" />
            </div>
          }
        />
      </div>
    </Card>
  );
};

const SupportEntertainerPopupContent: FC<{ entertainer: Entertainer; onClose: () => void }> = ({
  entertainer,
  onClose,
}) => {
  const [selectionRecord, setSelectionRecord] = useState<Record<string, boolean>>({});
  const { appSettings } = useAppSettingsQuery();
  const titleDivRef = useRef<HTMLDivElement>(null);
  const [contentTopPadding, setContentTopPadding] = useState(0);
  const { recommendEntertainerAsync } = useRecommendEntertainerMutation();

  useLayoutEffect(() => {
    setTimeout(() => {
      const rect = titleDivRef.current?.getBoundingClientRect();
      if (rect.height) {
        setContentTopPadding(rect.height - 16);
      }
    }, 250);
  }, []);

  const canSubmit = Object.values(selectionRecord).some((v) => v);

  const handleSelectAction = (key: string) => {
    setSelectionRecord((prev) => ({
      ...prev,
      [key]: !prev[key],
    }));
  };

  const handleSubmit = () => {
    const recommendationSlugs = Object.entries(selectionRecord)
      .filter(([, value]) => value)
      .map(([recommendationSlug]) => recommendationSlug);

    recommendEntertainerAsync({
      entertainerId: entertainer._id,
      questionSlugs: recommendationSlugs,
    });

    onClose();
  };

  return (
    <div style={{ position: 'relative' }}>
      <div
        ref={titleDivRef}
        style={{
          position: 'fixed',
          background: '#111',
          zIndex: 10,
          width: '100%',
          top: 0,
          left: 0,
          padding: '1.5rem 16px 0',
        }}
      >
        <TitleToolbar
          text={
            <div>
              <div
                style={{
                  textTransform: 'uppercase',
                  fontSize: '.7rem',
                  letterSpacing: '.1rem',
                  marginBottom: -4,
                  opacity: 0.7,
                }}
              >
                Support
              </div>
              <div>{entertainer.title}</div>
            </div>
          }
          size="md"
          action={
            <Button
              disabled={!canSubmit}
              roundness="rounded"
              size="sm"
              className="py-2 px-3"
              onClick={handleSubmit}
            >
              Recommend
            </Button>
          }
        />
      </div>

      <div className="d-grid gap-2" style={{ paddingTop: contentTopPadding }}>
        {Object.values(appSettings?.supportActions ?? {}).map(({ key, label, imgUrl }) => (
          <div
            key={key}
            className="d-flex gap-2 align-items-center py-1 px-2"
            style={{ backgroundColor: 'rgba(255,255,255,.1)', borderRadius: 8 }}
            onClick={() => handleSelectAction(key)}
          >
            <div style={{ fontSize: '1.1rem' }}>
              {selectionRecord[key] ? (
                <span style={{ color: 'var(--color-green)' }}>
                  <FontAwesomeIcon name="check-circle" />
                </span>
              ) : (
                <FontAwesomeIcon faStyle="far" name="circle" />
              )}
            </div>
            <img
              src={imgUrl}
              alt={label}
              style={{
                width: 30,
                height: 30,
                borderRadius: 8,
                ...(selectionRecord[key] ? {} : { filter: 'grayscale(1)', opacity: 0.5 }),
              }}
            />
            <strong>{label}</strong>
          </div>
        ))}
      </div>
    </div>
  );
};

const SupportEntertainerPopup: FC<{
  isVisible: boolean;
  onClose: () => void;
  entertainer?: Entertainer;
}> = ({ isVisible, onClose, entertainer }) => {
  return (
    <Popup isVisible={isVisible} onClose={onClose}>
      <Popup.Content>
        {entertainer && (
          <SupportEntertainerPopupContent entertainer={entertainer} onClose={onClose} />
        )}
      </Popup.Content>
    </Popup>
  );
};

interface SupportEntertainersScreenProps {}

interface SupportEntertainersScreenQueryParams {
  filter?: EntertainerFilter;
  cityId?: string;
  entertainerId?: string;
}

export const RecommendEntertainersScreen = createScreenComponent<
  SupportEntertainersScreenProps,
  SupportEntertainersScreenQueryParams
>(({ f7route }) => {
  const { filter = EntertainerFilter.All, cityId: cityIdOverride, entertainerId } = f7route.query;
  const [selectedEntertainerId, setSelectedEntertainerId] = useState<string>(entertainerId);

  const { currentCity } = useCurrentUserCity();
  const cityId = cityIdOverride ?? currentCity?._id;
  const { filteredEntertainerIdsAreReady, filteredEntertainerIds, filteredEntertainersTitle } =
    useFilteredEntertainerIdsQuery(cityId, 'dragPerformer', filter);
  const { entertainersAreReady, entertainers } = useHydratedEntertainersQuery(
    cityId,
    'dragPerformer',
  );

  const selectedEntertainer = useMemo(() => {
    if (!selectedEntertainerId || !entertainers) {
      return undefined;
    }

    return entertainers[selectedEntertainerId];
  }, [selectedEntertainerId, entertainers]);

  const filteredEntertainers = useMemo(() => {
    const unfilteredEntertainers = Object.values(entertainers ?? {});

    if (filter === EntertainerFilter.All) {
      return unfilteredEntertainers;
    }

    const filterRecord = Object.fromEntries(filteredEntertainerIds.map((id) => [id, true]));

    return unfilteredEntertainers.filter((entertainer) => {
      return filterRecord[entertainer._id];
    });
  }, [filter, entertainers, filteredEntertainerIds]);

  return (
    <Screen
      name="Support Entertainers Screen"
      headerProps={{
        title: <Screen.GagglTitle />,
      }}
      hideToolbar
      disableLocationPermissionGate
    >
      <Screen.Content maxWidth={425}>
        <TitleToolbar text={filteredEntertainersTitle ?? 'All Entertainers'} size="sm" />
        <p className="mt-0">
          <strong>Support Local Drag. Recommend Performers!</strong>
          <br />
          Your recommendations help shine a spotlight on the entertainers making an impact. Select a
          performer to show your support.
        </p>
        <p>
          ✨ The community evolves every week, so keep the love going by recommending and
          re-recommending your favorites regularly!
        </p>
        {((filteredEntertainerIdsAreReady && filter !== EntertainerFilter.All) ||
          filter === EntertainerFilter.All) &&
        entertainersAreReady ? (
          <div className="d-flex flex-column gap-2">
            {filteredEntertainers?.length ? (
              filteredEntertainers.map((entertainer) => (
                <LazyLoad key={entertainer._id}>
                  <EntertainerSupportCard
                    entertainer={entertainer}
                    onClick={({ _id }) => setSelectedEntertainerId(_id)}
                  />
                </LazyLoad>
              ))
            ) : (
              <EmptyState title="No entertainers found" text="There are no matching entertainers" />
            )}
          </div>
        ) : (
          <>
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
            <Skeleton height={40} />
          </>
        )}
      </Screen.Content>
      <SupportEntertainerPopup
        isVisible={!!selectedEntertainer}
        onClose={() => setSelectedEntertainerId(undefined)}
        entertainer={selectedEntertainer}
      />
    </Screen>
  );
});
